<template>
  <div>
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">{{ editId ? 'Edit Slider' : 'Add Slider' }}</h4>
        <b-button variant="primary" @click="resetForm">New Slider</b-button>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-1">
        {{ errorMessage }}
      </div>

      <div v-if="validationErrors.length" class="alert alert-danger py-1">
        <ul class="mb-0 pl-2">
          <li v-for="(err, index) in validationErrors" :key="index">
            {{ err }}
          </li>
        </ul>
      </div>

      <b-form @submit.prevent="save">
        <b-row>
          <b-col md="6">
            <b-form-group label="Title">
              <b-form-input v-model.trim="form.title" placeholder="Slider title" />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Subtitle">
              <b-form-input v-model.trim="form.subtitle" placeholder="Slider subtitle" />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Button Text">
              <b-form-input v-model.trim="form.button_text" placeholder="Shop Now" />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Button Link">
              <b-form-input v-model.trim="form.button_link" placeholder="/shop" />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Sort Order">
              <b-form-input v-model.number="form.sort_order" type="number" min="0" />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group label="Status">
              <b-form-select v-model="form.status" :options="statusOptions" />
            </b-form-group>
          </b-col>

          <b-col md="4">
            <b-form-group :label="editId ? 'Image (optional on edit)' : 'Image'">
              <b-form-file
                accept="image/*"
                @change="onFileChange"
                placeholder="Choose image"
                drop-placeholder="Drop image here"
              />
              <div class="mt-1 d-flex flex-wrap">
                <b-button variant="outline-primary" size="sm" class="mr-50 mb-50" @click="showMediaPicker = true">
                  Insert from Media
                </b-button>
                <small class="text-muted align-self-center mb-50">Upload or reuse existing media</small>
              </div>
            </b-form-group>
          </b-col>

          <b-col cols="12" v-if="imagePreview || selectedLibraryMedia">
            <div class="mb-2 d-flex align-items-start flex-wrap">
              <div class="position-relative mr-1 mb-1">
                <img
                  :src="selectedLibraryMedia ? (selectedLibraryMedia.preview_url || selectedLibraryMedia.original_url) : imagePreview"
                  alt="Slider preview"
                  style="width:180px;height:110px;object-fit:cover;border-radius:8px;border:1px solid #ddd;"
                />
                <b-button
                  v-if="selectedLibraryMedia"
                  size="sm"
                  variant="danger"
                  class="slider-remove-chip"
                  @click="clearSelectedLibraryMedia"
                >
                  ×
                </b-button>
              </div>
            </div>
          </b-col>

          <b-col cols="12">
            <b-button type="submit" variant="success" :disabled="saving" class="mr-1">
              {{ saving ? 'Saving...' : editId ? 'Update Slider' : 'Save Slider' }}
            </b-button>

            <b-button type="button" variant="outline-secondary" @click="resetForm">
              Reset
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">Slider List</h4>
      </div>

      <vue-good-table
        :columns="columns"
        :rows="rows"
        :line-numbers="true"
        :search-options="{ enabled: true }"
        :pagination-options="{ enabled: true, perPage: 10 }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'image'">
            <img
              v-if="props.row.image"
              :src="props.row.image"
              alt="Slider image"
              style="width:90px;height:55px;object-fit:cover;border-radius:6px;"
            />
            <span v-else>No image</span>
          </span>

          <span v-else-if="props.column.field === 'status'">
            <b-badge :variant="props.row.status ? 'success' : 'secondary'">
              {{ props.row.status ? 'Active' : 'Inactive' }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'action'">
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              @click="openEdit(props.row)"
            >
              Edit
            </b-button>

            <b-button
              size="sm"
              variant="outline-warning"
              class="mr-1"
              @click="toggleStatus(props.row)"
            >
              {{ props.row.status ? 'Disable' : 'Enable' }}
            </b-button>

            <b-button
              size="sm"
              variant="outline-danger"
              @click="deleteItem(props.row.id)"
            >
              Delete
            </b-button>
          </span>

          <span v-else>
            {{ props.row[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </b-card>

    <MediaPickerModal
      :visible="showMediaPicker"
      :multiple="false"
      :preselected="selectedLibraryMedia ? [selectedLibraryMedia] : []"
      title="Insert Slider Image"
      confirm-button-text="Insert Selected Image"
      @close="showMediaPicker = false"
      @select="handleSliderMediaInsert"
      @insert="handleSliderMediaInsert"
    />
  </div>
</template>

<script>
import axios from '@/helpers/axios'
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormFile,
  BButton,
  BBadge
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import 'vue-good-table/dist/vue-good-table.css'
import MediaPickerModal from '@/components/media/MediaPickerModal.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormFile,
    BButton,
    BBadge,
    VueGoodTable,
    MediaPickerModal
  },
  data() {
    return {
      rows: [],
      saving: false,
      editId: null,
      file: null,
      imagePreview: '',
      showMediaPicker: false,
      selectedLibraryMedia: null,
      errorMessage: '',
      validationErrors: [],
      form: {
        title: '',
        subtitle: '',
        button_text: '',
        button_link: '',
        sort_order: 0,
        status: 1,
      },
      statusOptions: [
        { value: 1, text: 'Active' },
        { value: 0, text: 'Inactive' },
      ],
      columns: [
        { label: 'ID', field: 'id' },
        { label: 'Image', field: 'image' },
        { label: 'Title', field: 'title' },
        { label: 'Subtitle', field: 'subtitle' },
        { label: 'Sort', field: 'sort_order' },
        { label: 'Status', field: 'status' },
        { label: 'Action', field: 'action' },
      ],
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    flattenErrors(errorsObj) {
      const messages = []
      if (!errorsObj) return messages

      Object.keys(errorsObj).forEach(key => {
        const val = errorsObj[key]
        if (Array.isArray(val)) {
          val.forEach(item => messages.push(item))
        } else if (val) {
          messages.push(String(val))
        }
      })

      return messages
    },

    async fetchData() {
      this.errorMessage = ''
      this.validationErrors = []

      try {
        const res = await axios.get('/api/v1/admin/sliders')
        if (res && res.data && res.data.data) {
          this.rows = res.data.data
        } else {
          this.rows = []
        }
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to load sliders'
      }
    },

    resetForm() {
      this.editId = null
      this.file = null
      this.imagePreview = ''
      this.selectedLibraryMedia = null
      this.errorMessage = ''
      this.validationErrors = []
      this.form = {
        title: '',
        subtitle: '',
        button_text: '',
        button_link: '',
        sort_order: 0,
        status: 1,
      }
    },

    openEdit(item) {
      this.editId = item.id
      this.file = null
      this.selectedLibraryMedia = null
      this.errorMessage = ''
      this.validationErrors = []
      this.form = {
        title: item.title || '',
        subtitle: item.subtitle || '',
        button_text: item.button_text || '',
        button_link: item.button_link || '',
        sort_order: item.sort_order || 0,
        status: item.status ? 1 : 0,
      }
      this.imagePreview = item.image || ''
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    onFileChange(e) {
      const files = e.target.files
      this.file = files && files[0] ? files[0] : null
      this.selectedLibraryMedia = null

      if (this.file) {
        this.imagePreview = URL.createObjectURL(this.file)
      }
    },

    handleSliderMediaInsert(media) {
      const item = Array.isArray(media) ? (media[0] || null) : media
      this.selectedLibraryMedia = item
      this.file = null
      this.imagePreview = item ? (item.preview_url || item.original_url || '') : ''
      this.showMediaPicker = false
    },

    clearSelectedLibraryMedia() {
      this.selectedLibraryMedia = null
      this.imagePreview = ''
    },

    async save() {
      this.saving = true
      this.errorMessage = ''
      this.validationErrors = []

      try {
        const formData = new FormData()
        formData.append('title', this.form.title || '')
        formData.append('subtitle', this.form.subtitle || '')
        formData.append('button_text', this.form.button_text || '')
        formData.append('button_link', this.form.button_link || '')
        formData.append('sort_order', this.form.sort_order !== undefined ? this.form.sort_order : 0)
        formData.append('status', this.form.status)

        if (this.file) {
          formData.append('image', this.file)
        } else if (this.selectedLibraryMedia && this.selectedLibraryMedia.id) {
          formData.append('library_media_id', this.selectedLibraryMedia.id)
        }

        if (this.editId) {
          await axios.post(`/api/v1/admin/sliders/${this.editId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        } else {
          await axios.post('/api/v1/admin/sliders', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }

        await this.fetchData()
        this.resetForm()
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Save failed'

        if (error && error.response && error.response.data && error.response.data.errors) {
          this.validationErrors = this.flattenErrors(error.response.data.errors)
        }
      } finally {
        this.saving = false
      }
    },

    async deleteItem(id) {
      if (!confirm('Are you sure you want to delete this slider?')) return

      try {
        await axios.delete(`/api/v1/admin/sliders/${id}`)
        await this.fetchData()

        if (this.editId === id) {
          this.resetForm()
        }
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Delete failed'
      }
    },

    async toggleStatus(item) {
      try {
        await axios.post(`/api/v1/admin/sliders/${item.id}/status`, {
          status: item.status ? 0 : 1,
        })
        await this.fetchData()
      } catch (error) {
        this.errorMessage = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Status update failed'
      }
    },
  },
}
</script>

<style scoped>
.slider-remove-chip {
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 999px;
  line-height: 1;
}
</style>
